import { either, taskEither } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { TaskEither } from 'fp-ts/lib/TaskEither';
import { ReactLeft } from 'utils/uiStates/uiStates';
import { errorToReactLeft } from 'utils/fp';
import { getDeploymentTE } from 'api/deployments/getDeployment';
import { listAllDeploymentsTE, listDeploymentsTE } from 'api/deployments/listDeployments';
import { listModelVersionsTE } from 'api/models/listModelVersions';
import { postDeploymentsTE } from 'api/deployments/postDeployments';
import { patchDeploymentsTE } from 'api/deployments/patchDeployments';
import { deleteDeploymentsTE } from 'api/deployments/deleteDeployments';
import { DeploymentsRequestBody } from 'api/nodepools/types';
import { displayError } from '../apiUtils';

export const deploymentsApiEffects = {
  getDeploymentTE: (userId: string, deploymentId: string): TaskEither<ReactLeft, CF.API.Compute.Deployment> =>
    pipe(
      getDeploymentTE({ userId, deploymentId }, errorToReactLeft),
      taskEither.map((x) => x.deployment),
    ),
  listDeploymentsTE: (userId: string, nodePoolId?: string, modelVersionIds?: string[]) =>
    pipe(
      listDeploymentsTE({ userId, nodePoolId, modelVersionIds }, errorToReactLeft),
      taskEither.map((x) => x.deployments),
    ),
  listAllDeploymentsTE: (userId: string) =>
    pipe(
      listAllDeploymentsTE({ userId }, errorToReactLeft),
      taskEither.map((x) => x.deployments),
    ),
  listModelVersionsTE: (modelId: string, appId: string, userId: string, sessionToken?: string): TaskEither<ReactLeft, CF.API.Models.ModelVersion[]> =>
    pipe(
      listModelVersionsTE(
        {
          modelId,
          appId,
          userId,
          sessionToken,
        },
        errorToReactLeft,
      ),
      taskEither.map((x) => x.model_versions),
    ),
  postDeploymentsTE: (userId: string, body: DeploymentsRequestBody) =>
    pipe(
      postDeploymentsTE({
        userId,
        body,
      }),
      taskEither.map((resp) => resp),
    ),
  patchDeploymentsTE: (userId: string, body: DeploymentsRequestBody) =>
    pipe(
      patchDeploymentsTE({ userId, body }),
      taskEither.map((resp) => resp),
    ),
  deleteDeploymentsTE: (userId: string, deploymentIds: string[]) =>
    pipe(
      deleteDeploymentsTE({ userId, deploymentIds }),
      taskEither.map((resp) => resp),
    ),
  createDeployment: async (userId: string, requestBody: DeploymentsRequestBody) => {
    const deploymentResponse = await deploymentsApiEffects.postDeploymentsTE(userId, requestBody)();

    return pipe(
      deploymentResponse,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return false;
        },
        () => {
          return true;
        },
      ),
    );
  },
  editDeployment: async (userId: string, requestBody: DeploymentsRequestBody) => {
    const deploymentResponse = await deploymentsApiEffects.patchDeploymentsTE(userId, requestBody)();

    return pipe(
      deploymentResponse,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return false;
        },
        () => {
          return true;
        },
      ),
    );
  },
};
